import type { MouseEventHandler } from 'react'
import type { IntlShape } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import { showTripPlannerSnackbar } from 'tripPlanner/components/Snackbar/TripPlannerSnackbarHost'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

// A determinate-looking arc over a full track, per the design. The repo's LineSpinnerIcon is
// a ring of dots, which reads differently, so this draws the arc directly.
const SPINNER_SIZE = 20
const SPINNER_STROKE = 2
const SPINNER_CENTRE = SPINNER_SIZE / 2
const SPINNER_RADIUS = (SPINNER_SIZE - SPINNER_STROKE) / 2
const SPINNER_CIRCUMFERENCE = 2 * Math.PI * SPINNER_RADIUS
const SPINNER_ARC_LENGTH = SPINNER_CIRCUMFERENCE * 0.28

const SpinnerSvg = styled.svg`
  flex-shrink: 0;
  animation: ${spin} 800ms linear infinite;
`

const SpinnerTrack = styled.circle`
  stroke: ${props => props.theme.palette.neutral.default.five};
`

const SpinnerArc = styled.circle`
  stroke: ${props => props.theme.palette.neutral.default.one};
`

function AddingToTripSpinner() {
  return (
    <SpinnerSvg
      width={SPINNER_SIZE}
      height={SPINNER_SIZE}
      viewBox={`0 0 ${SPINNER_SIZE} ${SPINNER_SIZE}`}
      fill="none"
      aria-hidden="true"
    >
      <SpinnerTrack
        cx={SPINNER_CENTRE}
        cy={SPINNER_CENTRE}
        r={SPINNER_RADIUS}
        strokeWidth={SPINNER_STROKE}
      />
      <SpinnerArc
        cx={SPINNER_CENTRE}
        cy={SPINNER_CENTRE}
        r={SPINNER_RADIUS}
        strokeWidth={SPINNER_STROKE}
        strokeLinecap="round"
        strokeDasharray={`${SPINNER_ARC_LENGTH} ${SPINNER_CIRCUMFERENCE - SPINNER_ARC_LENGTH}`}
        transform={`rotate(-90 ${SPINNER_CENTRE} ${SPINNER_CENTRE})`}
      />
    </SpinnerSvg>
  )
}

/**
 * Optimistic "Adding to trip" toast, raised the moment the user picks a day. The add itself
 * completes asynchronously over the chat stream, so the matching success toast is raised
 * separately from useItineraryUpdates once the item lands — enqueuing it closes this one.
 */
export function showAddingToTripSnackbar(intl: IntlShape, placeName: string) {
  showTripPlannerSnackbar({
    description: placeName,
    kind: 'neutral',
    heading: intl.formatMessage({
      id: 'o1FhOaeNDDoe5Qhkml_Z',
      defaultMessage: 'Adding to trip',
      description: 'Snackbar heading shown while a place is being added to the traveller itinerary and the request is still in flight',
    }),
    icon: <AddingToTripSpinner />,
    // Held open until the itinerary update lands and swaps in the confirmation, so the
    // traveller never sees a gap between "Adding to trip" and "Added to trip".
    pending: true,
  })
}

/**
 * "Added to trip" confirmation. The View action takes the traveller to the Timeline
 * and scrolls the newly added item into view.
 */
export function showAddedToTripSnackbar(
  intl: IntlShape,
  itemName: string,
  onView: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>,
) {
  showTripPlannerSnackbar({
    description: itemName,
    kind: 'success',
    heading: intl.formatMessage({
      id: 'EkhV7UlntGcasG6J6OgV',
      defaultMessage: 'Added to trip',
      description: 'Snackbar heading confirming that a place has been added to the traveller itinerary successfully',
    }),
    icon: <SolidCheckCircleIcon colour="success" size="S" />,
    action: {
      label: intl.formatMessage({
        id: 'f9skI5_tiGxV7BBwsmDN',
        defaultMessage: 'View',
        description: 'Snackbar action that opens the Timeline view and scrolls to the item that was just added to the trip',
      }),
      onAction: onView,
      testId: 'added-to-trip-view',
    },
  })
}
